/** Audio / Asset compact node — canonical first-class "音频资产" Asset
 * Node (VistoeraTASK-00115) + inline expand + Audio Editor entry (TASK-00165)
 * + Header/Body/Footer visual grammar unified with `ImageAssetNode` and
 * an immediately-visible pending/failed sibling lifecycle for async trim
 * saves (VistoeraTASK-00171).
 *
 * Structural parity with `ImageAssetNode.tsx` (spec §1/§2/§18):
 *   Header — icon + generic node-type label ("音频资产") + hover toolbar
 *     (下载/复制/更换音频/删除), NO click handler of its own — a click
 *     bubbles straight through to `CanvasBoard.onNodeClick`, which now
 *     treats `audio_asset` exactly like `media`/`video_media`/`text_asset`
 *     (see that function's own doc comment) — the SAME canonical expand
 *     primitive, never a second, node-local, stopPropagation-based one.
 *   Body — real audio title + player, no more "black media-placeholder
 *     panel" background (that variable is meant to letterbox an actual
 *     <img>, not sit behind a translucent player row); body is card-
 *     colored chrome the player floats on top of, exactly the visual
 *     weight Image/Video Asset bodies carry.
 *   Footer — "编辑音频" is a `.pcv-gen-inspector` block `Button`, the
 *     SAME primitive `ImageAssetNode`'s "编辑图片" uses, never floating
 *     inside the body/player area.
 */

import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  LoadingOutlined,
  ReloadOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Button, Tooltip } from 'antd'
import { memo, useEffect, type MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { useNodeDefinition } from '../nodeRegistry'
import { canvasNodeShellStateClass } from '../canvasNodeShellV2'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { downloadCanvasMedia } from '../canvasDownload'
import { canvasAudioPlaybackUrl } from '../audioGenerationProvider'
import CanvasAudioAssetCompactPlayer from '../CanvasAudioAssetCompactPlayer'
import type { FlowNodeData } from '../types'

const AUDIO_ASSET_CARD_WIDTH = 300

interface AudioAssetSnapshot {
  name?: string | null
  presentation_type?: string | null
}

function readAudioAssetSnapshot(fields: Record<string, unknown> | undefined): AudioAssetSnapshot | null {
  const raw = fields?.audioAssetSnapshot
  if (!raw || typeof raw !== 'object') return null
  return raw as AudioAssetSnapshot
}

function AudioAssetNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const label = (nodeData.fields?.label as string | undefined) || nodeLabel(def.nodeType, def.label)
  const entityId = nodeData.entityId
  const workspaceId = nodeData.workspaceId
  const snapshot = readAudioAssetSnapshot(nodeData.fields)
  // VistoeraTASK-00220 — client-only upload lifecycle for a node created
  // from a local OS file drop, before its canonical asset exists (same
  // `localUpload` contract `ImageAssetNode.tsx` already ships — see
  // `types.ts`'s doc comment for the full contract).
  const localUpload = nodeData.localUpload || null
  // VistoeraTASK-00171 §八/§九/§十 — this node IS the final sibling from
  // the moment "保存为新音频" is accepted (same `entityId`/identity all
  // the way through), rendering a lightweight pending/failed body
  // instead of a real player until the async server-side trim resolves.
  const trimPending = Boolean(nodeData.fields?.audioTrimPending)
  const trimFailed = Boolean(nodeData.fields?.audioTrimFailed)
  const trimError = (nodeData.fields?.audioTrimError as string | undefined) || null
  const isReadyForPlayback = Boolean(entityId) && !trimPending && !trimFailed
  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showInspector = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)
  const updateNodeInternals = useUpdateNodeInternals()

  // Never fetch/mount the `<audio>` element (no bytes, no waveform) while
  // pending or failed — spec §十六 "Pending node: 不得 fetch audio".
  const playbackUrl = isReadyForPlayback && entityId && workspaceId ? canvasAudioPlaybackUrl(workspaceId, entityId) : null
  // A still-local (never persisted) upload placeholder counts as
  // "content" for width/click-gating purposes even though `entityId`
  // is still null (spec §四 — dropping a file must render a real card
  // immediately, never fall back to the empty "点击选择音频" affordance
  // while a real upload is in flight/failed).
  const hasContent = Boolean(entityId) || Boolean(localUpload)

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [id, showInspector, trimPending, trimFailed, updateNodeInternals])

  const openPicker = () => {
    if (!workspaceId || !nodeData.onOpenAudioAssetPicker) return
    nodeData.onOpenAudioAssetPicker({
      nodeId: id,
      title: entityId ? t('mediaCard.replaceAudio') : t('mediaCard.selectAudio'),
      lockPresentationType: false,
      allowedCategories: ['SPEECH', 'MUSIC', 'SFX', 'AMBIENCE', 'RECORDING', 'REFERENCE_AUDIO', 'MIXDOWN'],
      initialCategory: 'all',
      initialSelection: entityId ? `asset:${entityId}` : null,
      binding: 'audio_asset',
    })
  }

  const handleEmptyClick = () => {
    openPicker()
  }

  const handleSwapClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    openPicker()
  }

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }

  const handleDownloadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!playbackUrl) return
    downloadCanvasMedia(`${playbackUrl}${playbackUrl.includes('?') ? '&' : '?'}download=true`)
  }

  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    // A still-local (never persisted) upload placeholder has no
    // Canvas-node-delete-confirm / asset-lifecycle concerns yet — same
    // rationale as `ImageAssetNode`'s own `handleDeleteClick`.
    if (localUpload) {
      nodeData.onRemoveLocalAudioUpload?.(id)
      return
    }
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const handleRetryLocalUploadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onRetryLocalAudioUpload?.(id)
  }

  const handleRemoveLocalUploadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onRemoveLocalAudioUpload?.(id)
  }

  const openEditor = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!workspaceId || !entityId || !nodeData.onOpenAudioEditor) return
    nodeData.onOpenAudioEditor({ nodeId: id, assetId: entityId, workspaceId })
  }

  const handleRetryClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onRetryAudioTrim?.(id)
  }

  return (
    <div
      className={`pcv-node pcv-node--media pcv-node--audio-asset${selected ? ' is-selected' : ''}${!hasContent ? ' is-empty' : ''} ${canvasNodeShellStateClass(showInspector)}`}
      style={{ width: hasContent ? AUDIO_ASSET_CARD_WIDTH : undefined }}
      onClick={hasContent ? undefined : handleEmptyClick}
      data-testid="canvas-audio-asset-node"
      data-node-id={id}
    >
      {/* Left/right connection Handles are direct children of the outer
       * node div — same P1 rationale as `ImageAssetNode` (never nested
       * inside `.pcv-node-inner`, so its `overflow:hidden` never clips a
       * Handle straddling the card's own edge). */}
      <Handle type="target" position={Position.Left} className="pcv-handle pcv-handle--lg" />
      <div className="pcv-node-inner">
        <div className="pcv-media-header">
          <span className="pcv-media-icon" style={{ color: def.color, background: `${def.color}22` }}>
            {def.icon}
          </span>
          <span className="pcv-media-title" title={label}>
            {label}
          </span>
          <div className="pcv-media-hover-toolbar nodrag">
            {isReadyForPlayback ? (
              <Tooltip title={t('common.download')} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('common.download')} onClick={handleDownloadClick}>
                  <DownloadOutlined />
                </button>
              </Tooltip>
            ) : null}
            {hasContent ? (
              <Tooltip title={t('common.copy')} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('common.copy')} onClick={handleCopyClick}>
                  <CopyOutlined />
                </button>
              </Tooltip>
            ) : null}
            {isReadyForPlayback ? (
              <Tooltip title={t('mediaCard.replaceAudio')} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('mediaCard.replaceAudio')} onClick={handleSwapClick}>
                  <SwapOutlined />
                </button>
              </Tooltip>
            ) : null}
            <Tooltip title={t('common.delete')} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" aria-label={t('common.delete')} onClick={handleDeleteClick}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </div>
        </div>
        {/* Result/body click expands (spec §四) — no local onClick here at
         * all: this bubbles straight to `CanvasBoard.onNodeClick`, which
         * now owns `audio_asset` expansion exactly like `media`. Only
         * genuinely interactive children (play/scrubber/footer button/
         * header actions/handles) ever call `stopPropagation`. */}
        <div className="pcv-media-body pcv-audio-asset-body">
          {localUpload?.status === 'uploading' ? (
            <div className="pcv-media-local-upload-overlay" data-testid="audio-asset-uploading">
              <LoadingOutlined spin />
              <span className="pcv-media-local-upload-filename">{localUpload.filename}</span>
              <span className="pcv-media-local-upload-label">{t('mediaCard.uploadingAudio')}</span>
            </div>
          ) : localUpload?.status === 'reconciling' ? (
            /* VistoeraTASK-00232 - same non-terminal reconciling overlay
             * ImageAssetNode ships (spec section 16, "Ambiguous Timeout
             * != FAILED") - no Retry/Remove here, resolves automatically
             * within one round-trip to either success (overlay
             * disappears) or 'error' below. */
            <div className="pcv-media-local-upload-overlay" data-testid="audio-asset-reconciling">
              <LoadingOutlined spin />
              <span className="pcv-media-local-upload-filename">{localUpload.filename}</span>
              <span className="pcv-media-local-upload-label">{t('mediaCard.confirmingUploadStatus')}</span>
            </div>
          ) : localUpload?.status === 'error' ? (
            <div className="pcv-media-local-upload-overlay pcv-media-local-upload-overlay--error nodrag" data-testid="audio-asset-upload-failed">
              <span className="pcv-media-local-upload-label">{t('mediaCard.uploadAudioFailed')}</span>
              <div className="pcv-media-local-upload-actions">
                <Button size="small" icon={<ReloadOutlined />} onClick={handleRetryLocalUploadClick} data-testid="audio-asset-retry-upload">
                  {t('common.retry')}
                </Button>
                <Button size="small" danger icon={<CloseOutlined />} onClick={handleRemoveLocalUploadClick} data-testid="audio-asset-remove-upload">
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ) : trimPending ? (
            <div className="pcv-audio-asset-pending" data-testid="audio-trim-save-pending">
              <LoadingOutlined spin />
              <span>{t('audioEditor.trimSaving', { defaultValue: '正在生成音频…' })}</span>
            </div>
          ) : trimFailed ? (
            <div className="pcv-audio-asset-failed nodrag" data-testid="audio-trim-save-error">
              <span className="pcv-audio-asset-failed-text">
                {trimError || t('audioEditor.trimFailed', { defaultValue: '音频生成失败' })}
              </span>
              <div className="pcv-audio-asset-failed-actions">
                {nodeData.onRetryAudioTrim ? (
                  <Button size="small" icon={<ReloadOutlined />} onClick={handleRetryClick} data-testid="audio-trim-retry">
                    {t('common.retry', { defaultValue: '重试' })}
                  </Button>
                ) : null}
                <Button size="small" danger icon={<CloseOutlined />} onClick={handleDeleteClick} data-testid="audio-trim-delete">
                  {t('common.delete', { defaultValue: '删除' })}
                </Button>
              </div>
            </div>
          ) : playbackUrl ? (
            <div className="pcv-audio-asset-player-wrap nodrag nopan">
              <CanvasAudioAssetCompactPlayer playbackUrl={playbackUrl} testId="canvas-audio-asset-player" title={snapshot?.name} />
            </div>
          ) : (
            <div className="pcv-media-placeholder">
              {def.icon}
              {!hasContent ? <span className="pcv-media-placeholder-hint">{t('mediaCard.clickToSelectAudio')}</span> : null}
            </div>
          )}
        </div>
      </div>

      {/* Footer action — same `.pcv-gen-inspector` block-Button primitive
       * `ImageAssetNode`'s "编辑图片" uses (spec §二.6/§二.7), never a
       * button floating over the player. Only meaningful once the audio
       * is actually ready to edit. */}
      {showInspector && isReadyForPlayback ? (
        <div className="pcv-gen-inspector nodrag" data-testid="audio-asset-expanded">
          <Button block icon={<EditOutlined />} onClick={openEditor} disabled={!workspaceId || !entityId} data-testid="audio-asset-edit-button">
            {t('audioEditor.editButton', { defaultValue: '编辑音频' })}
          </Button>
        </div>
      ) : null}

      <Handle type="source" position={Position.Right} id="audio-out" className="pcv-handle pcv-handle--lg" />
    </div>
  )
}

export default memo(AudioAssetNodeImpl)
